import React, { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { InputTextarea } from 'primereact/inputtextarea';
import { AuthContext } from '../../../Header/auth - context';
import { uzmiGresku } from '../../../custom/greske';
import './SaTelefonaTab.css';

/* Ture → Obračun → Troškovi → „Troškovi vozača".

   Troškovi koje vozač pošalje sa telefona stoje u `trip.VozacTrosak` i NISU
   troškovi ture — ni u jedan zbir ne ulaze. Operater ih ovde pregleda:

     Prihvati → nastaje STVARAN trošak u `trip.Troskovi`, koji operater dalje
                menja kao i svaki svoj (iznos, kurs, kartica…)
     Odbij    → uz razlog, koji vozač vidi na telefonu i može da ispravi

   Vozačev unos se ni u jednom slučaju ne menja — ostaje zapis šta je vozač
   zaista poslao, uz vezu na trošak koji je od njega nastao. */

const STATUS = { 0: 'ceka', 1: 'prihvaceno', 2: 'odbijeno' };

const VozacTroskoviDialog = ({ visible, onHide, unosi, onPromena }) => {
    const { t } = useTranslation();
    const auth = useContext(AuthContext);
    const toast = useRef(null);

    const [radi, setRadi] = useState(null);           // id unosa koji se obrađuje
    const [odbijanje, setOdbijanje] = useState(null); // { id, razlog }
    const [slika, setSlika] = useState(null);

    /* REACT_APP_BACKEND_URL se završava na /API, a putanje priloga su od korena
       uploada — isti obrazac kao u SaTelefonaTab. */
    const osnova = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/API\/?$/, '');

    const obradi = async (ruta, telo, podrazumevanaGreska) => {
        setRadi(telo.id);
        try {
            const res = await fetch(process.env.REACT_APP_BACKEND_URL + ruta, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
                body: JSON.stringify(telo),
            });
            if (!res.ok) throw res;
            const d = await res.json();
            toast.current?.show({ severity: 'success', summary: t('common.uspeh'), detail: d.message, life: 3500 });
            setOdbijanje(null);
            /* Osvežava i listu vozačevih unosa i tabelu stvarnih troškova —
               prihvatanjem se pojavio nov red i u troškovniku. */
            onPromena?.();
        } catch (e) {
            const g = await uzmiGresku(e, podrazumevanaGreska);
            toast.current?.show({ severity: 'error', summary: t('common.greska'), detail: g.message, life: 5000 });
        } finally {
            setRadi(null);
        }
    };

    const teloStatusa = (r) => {
        const stanje = STATUS[r.statusObrade] || 'ceka';
        return <span className={`st-status st-status-${stanje}`}>{t(`saTelefona.status_${stanje}`)}</span>;
    };

    const teloIznosa = (r) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {Number(r.vrednostValuta).toLocaleString('sr-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {r.Valuta || ''}
        </span>
    );

    const teloRacuna = (r) => (
        Number(r.imaSliku) === 1
            ? <Button type="button" icon="pi pi-image" className="p-button-text p-button-sm"
                      onClick={() => setSlika(osnova + r.racunDokument)} />
            : <span className="st-meta">—</span>
    );

    const teloRadnji = (r) => {
        if (Number(r.turaZakljucana) === 1) return <span className="st-meta">{t('saTelefona.turaZakljucana')}</span>;
        if (Number(r.statusObrade) === 1) return <span className="st-meta">{t('saTelefona.uTroskovniku')}</span>;
        if (Number(r.statusObrade) === 2) return <span className="st-meta st-odbijeno">{r.razlogOdbijanja}</span>;
        return (
            <div style={{ display: 'flex', gap: '0.35rem' }}>
                <Button type="button" label={t('saTelefona.prihvati')} icon="pi pi-check" severity="success"
                        className="p-button-sm" loading={radi === r.id}
                        onClick={() => obradi('/trip/VozacTrosakPrihvati', { id: r.id }, t('saTelefona.greskaObrade'))} />
                <Button type="button" label={t('saTelefona.odbij')} icon="pi pi-times" severity="danger" outlined
                        className="p-button-sm" disabled={radi === r.id}
                        onClick={() => setOdbijanje({ id: r.id, razlog: '' })} />
            </div>
        );
    };

    return (
        <>
            <Toast ref={toast} />
            <Dialog header={t('saTelefona.troskoviNaslov')} visible={visible} onHide={onHide}
                    style={{ width: '82rem', maxWidth: '96vw' }}>
                <DataTable value={unosi || []} paginator rows={10} scrollable scrollHeight="55vh"
                           resizableColumns columnResizeMode="expand" className="turePregledTable"
                           emptyMessage={t('saTelefona.nemaTroskova')}>
                    <Column field="Datum" header={t('ture.datumTroska')} style={{ minWidth: '110px' }} />
                    <Column field="Vozac" header={t('ture.vozac')} style={{ minWidth: '160px' }} />
                    <Column field="Vrsta" header={t('ture.vrstaTroska')} style={{ minWidth: '150px' }} />
                    <Column field="Nacin" header={t('ture.nacinPlacanja')} style={{ minWidth: '140px' }} />
                    <Column field="Drzava" header={t('ture.drzava')} style={{ minWidth: '140px' }} />
                    <Column header={t('nova.iznos')} body={teloIznosa} style={{ minWidth: '130px' }} />
                    <Column field="kilometraza" header={t('ture.kilometraza')} style={{ minWidth: '110px' }} />
                    <Column field="napomena" header={t('saTelefona.napomena')} style={{ minWidth: '180px' }} />
                    <Column header={t('ture.racunSlika')} body={teloRacuna} style={{ minWidth: '90px' }} />
                    <Column field="Poslato" header={t('saTelefona.poslato')} style={{ minWidth: '140px' }} />
                    <Column header={t('nova.status')} body={teloStatusa} style={{ minWidth: '120px' }} />
                    <Column header="" body={teloRadnji} style={{ minWidth: '230px' }} />
                </DataTable>
            </Dialog>

            <Dialog header={t('saTelefona.odbijTrosakNaslov')} visible={!!odbijanje}
                    style={{ width: '32rem', maxWidth: '96vw' }} onHide={() => setOdbijanje(null)}>
                {odbijanje && (
                    <div className="st-odbij-forma">
                        <label htmlFor="vt_razlog">{t('saTelefona.razlog')}</label>
                        <InputTextarea id="vt_razlog" rows={3} autoResize value={odbijanje.razlog}
                                       onChange={(e) => setOdbijanje({ ...odbijanje, razlog: e.target.value })}
                                       placeholder={t('saTelefona.razlogPh')} />
                        <div className="st-dugmad">
                            <button type="button" className="btn btn-secondary" onClick={() => setOdbijanje(null)}>
                                {t('common.otkazi')}
                            </button>
                            <button type="button" className="btn btn-danger"
                                    disabled={!odbijanje.razlog.trim() || radi === odbijanje.id}
                                    onClick={() => obradi('/trip/VozacTrosakOdbij',
                                        { id: odbijanje.id, razlog: odbijanje.razlog.trim() }, t('saTelefona.greskaObrade'))}>
                                {t('saTelefona.odbij')}
                            </button>
                        </div>
                    </div>
                )}
            </Dialog>

            <Dialog header={t('ture.racunSlika')} visible={!!slika} style={{ width: '40rem', maxWidth: '96vw' }}
                    onHide={() => setSlika(null)}>
                {slika && <img src={slika} alt={t('ture.racunSlika')} className="st-slika-velika" />}
            </Dialog>
        </>
    );
};

export default VozacTroskoviDialog;
